import React, {useEffect, useRef, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {
	MdAccountBalanceWallet,
	MdCalendarToday,
	MdCheckCircle,
	MdClose,
	MdFreeCancellation,
	MdMarkChatRead,
	MdNotifications,
	MdOutlineNotifications,
} from 'react-icons/md';
import {Notification} from '../types/Notification';
import {
	Box,
	Heading,
	HStack,
	Icon,
	IconButton,
	SlideFade,
	VStack,
	Text,
	Spacer,
	Portal,
	useBreakpointValue,
	Button,
	BoxProps,
} from 'components/ui/chakra';
import NotificationController from 'puddleglum/api/NotificationController';

type NotificationBellProps = BoxProps & {
	placement?: 'below' | 'above';
};

const NotificationBell = ({placement = 'below', ...boxProps}: NotificationBellProps) => {
	const bellRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	const isMobile = useBreakpointValue({
		base: true,
		md: false,
	});

	const [showNotifications, setShowNotifications] = useState(false);

	useEffect(() => {
		if (!showNotifications) return undefined;

		const closeIfOutside = (event: MouseEvent | TouchEvent) => {
			const target = event.target as Node | null;

			if (!target) return;
			if (bellRef.current?.contains(target)) return;
			if (contentRef.current?.contains(target)) return;

			setShowNotifications(false);
		};

		document.addEventListener('mousedown', closeIfOutside);
		document.addEventListener('touchstart', closeIfOutside);

		return () => {
			document.removeEventListener('mousedown', closeIfOutside);
			document.removeEventListener('touchstart', closeIfOutside);
		};
	}, [showNotifications]);

	const {data: notifications, refetch: refetchNotifications} = useQuery<Notification[]>(
		['notifications'],
		async () => {
			const {data} = await NotificationController.getNotifications();
			return data;
		},
	);

	const markAsRead = async (notification: Notification) => {
		await NotificationController.markAsRead(notification.id);

		refetchNotifications();
	};

	const markAllNotificationsAsRead = async () => {
		await NotificationController.markAllAsRead();
		refetchNotifications();
	};

	const hasUnreadNotifications = (notifications ?? []).some(
		(notification) => !notification.read_at,
	);

	const getIcon = (type: string) => {
		if (type.includes('AppointmentConfirmed'))
			return <Icon as={MdCheckCircle} boxSize={6} color='olive.500' />;
		if (type.includes('AppointmentCanceled'))
			return <Icon as={MdFreeCancellation} boxSize={6} color='red.500' />;
		if (type.includes('AppointmentRequested'))
			return <Icon as={MdCalendarToday} boxSize={6} color='clay.500' />;
		if (type.includes('OverdueBalance'))
			return <Icon as={MdAccountBalanceWallet} boxSize={6} color='blue.500' />;

		return <Icon as={MdOutlineNotifications} boxSize={6} color='stone.500' />;
	};

	function getNotificationContents() {
		const boxPropsIfNotMobile = isMobile ? {} : boxProps;

		// On desktop, 'above' anchors the panel to the bell's actual screen position so it
		// opens upward from a bottom-anchored bell instead of dropping from the page top.
		const bellRect =
			!isMobile && placement === 'above'
				? bellRef.current?.getBoundingClientRect()
				: undefined;
		const anchorProps =
			!isMobile && placement === 'above'
				? {
						position: 'fixed' as const,
						bottom: bellRect ? window.innerHeight - bellRect.top + 12 : 0,
						left: bellRect ? bellRect.left : 0,
					}
				: {
						position: {base: 'fixed', md: 'absolute'} as const,
						mt: {base: 0, md: '70px'},
						top: 0, // This is the top of the screen
						left: !isMobile && bellRef.current ? bellRef.current.offsetLeft - 420 : 0,
					};

		return (
			<Box
				ref={contentRef}
				zIndex='popover'
				w={{base: '100%', md: '440px'}}
				{...anchorProps}
				{...boxPropsIfNotMobile}
			>
				<SlideFade in={showNotifications} unmountOnExit>
					<Box
						w='auto'
						maxH={{base: '100vh', md: 'calc(100vh - 88px - 100px)'}}
						h={{base: '100vh', md: 'inherit'}}
						p={4}
						bgColor='panel'
						borderRadius='panel'
						border='solid 1px'
						borderColor='border'
						boxShadow='0 24px 45px rgba(79, 74, 68, 0.12)'
						overflowY='auto'
					>
						<HStack w='full' justifyContent='space-between'>
							<Heading size='md'>Notifications</Heading>
							<IconButton
								variant='ghost'
								aria-label='Close'
								icon={<Icon as={MdClose} />}
								onClick={() => setShowNotifications(false)}
							/>
						</HStack>

						{notifications?.length === 0 && (
							<Text color='subtleText' mt={4}>
								You have no notifications.
							</Text>
						)}

						<VStack spacing={4} mt={4}>
							{(notifications ?? []).map((notification) => (
								<HStack
									key={notification.id}
									w='full'
									bgColor={notification.read_at ? 'panelMuted' : 'sandstone.50'}
									p={4}
									borderRadius='xl'
									border='solid 1px'
									borderColor={
										notification.read_at ? 'border.subtle' : 'sandstone.200'
									}
								>
									<Box w='32px'>{getIcon(notification.type)}</Box>
									<Box flex={1}>
										<HStack justifyContent='space-between' w='full'>
											<Heading size='sm'>
												{!notification.read_at && (
													<Box
														display='inline-block'
														bgColor='clay.500'
														borderRadius='full'
														w={2}
														h={2}
														mr={2}
													/>
												)}
												{notification.title}
											</Heading>
											<Text fontSize='xs'>{notification.created_at}</Text>
										</HStack>
										<HStack w='full'>
											<Text noOfLines={2} color='subtleText' fontSize='sm'>
												{notification.body}
											</Text>
											<Spacer />
											<HStack>
												{!notification.read_at && (
													<IconButton
														variant='ghost'
														size='sm'
														aria-label='Mark as read'
														icon={<Icon as={MdMarkChatRead} />}
														onClick={() => markAsRead(notification)}
													/>
												)}
											</HStack>
										</HStack>
									</Box>
								</HStack>
							))}
						</VStack>

						{hasUnreadNotifications && (
							<Button
								w='full'
								mt={4}
								colorScheme='blue'
								onClick={markAllNotificationsAsRead}
							>
								Mark all as read
							</Button>
						)}
					</Box>
				</SlideFade>
			</Box>
		);
	}

	return (
		<>
			<Box
				ref={bellRef}
				pos='relative'
				role='button'
				color={hasUnreadNotifications ? 'clay.500' : 'stone.400'}
				_hover={{color: 'clay.400'}}
				onClick={() => setShowNotifications(true)}
			>
				<Icon
					as={hasUnreadNotifications ? MdNotifications : MdOutlineNotifications}
					boxSize={6}
				/>
				{hasUnreadNotifications && (
					<Box
						pos='absolute'
						top={-1}
						right={-1}
						bgColor='clay.500'
						borderRadius='full'
						w={2}
						h={2}
					/>
				)}
			</Box>
			{/*
			 * Portal escapes the sidebar's `backdropFilter` ancestor, which otherwise turns
			 * into a containing block for `position: fixed` descendants and clips them.
			 */}
			{isMobile || placement === 'above' ? (
				<Portal>{getNotificationContents()}</Portal>
			) : (
				getNotificationContents()
			)}
		</>
	);
};

export default NotificationBell;
